/**
 * Permission constants and types.
 * Mirrored from @bonsai/shared — keep in sync when permissions change.
 */
export const Permission = {
  READ_INVENTORY: 'read:inventory',
  WRITE_INVENTORY: 'write:inventory',
  DELETE_INVENTORY: 'delete:inventory',
  READ_WORKSHOPS: 'read:workshops',
  WRITE_WORKSHOPS: 'write:workshops',
  DELETE_WORKSHOPS: 'delete:workshops',
  READ_VENUES: 'read:venues',
  WRITE_VENUES: 'write:venues',
  DELETE_VENUES: 'delete:venues',
  READ_BOOKINGS: 'read:bookings',
  WRITE_BOOKINGS: 'write:bookings',
  /** Manual COMP booking creation — a free booking born Confirmed (BON-142).
   *  Deliberately narrower than `write:bookings`: comping is an abuse vector
   *  (unattributed free tickets), so it's manager-gated separately. */
  WRITE_COMP_BOOKINGS: 'write:comp-bookings',
  /** Resolving / reopening a flagged payment review (BON-381). Deliberately
   *  narrower than `read:bookings`, which every reviewer holds: clearing a
   *  money-affecting flag is a stamped, audited action on a money surface, so
   *  it is granted separately (Admin + Regional Director). */
  WRITE_PAYMENT_REVIEWS: 'write:payment-reviews',
  READ_CUSTOMERS: 'read:customers',
  /** Reading private events. `write:workshops` also reads them, as the backend
   *  private-events routes allow. */
  READ_PRIVATE_EVENTS: 'read:private_events',
  /** Read-only Customers › Gift cards surface (BON-223). Deliberately NOT
   *  OR'd with `read:all` on the backend guard — RD/Finance/Marketing hold
   *  `read:all` but must not see gift-card balances or ledgers, so this stays
   *  its own narrow scope (Admin + CSR only). */
  READ_GIFT_CARDS: 'read:gift_cards',
  /** Issue a gift card by hand. Needs `read:gift_cards` as well. */
  ISSUE_GIFT_CARDS: 'issue:gift_cards',
  /** The staff gift-pass list and detail pages. Admin, CSR and Finance. */
  READ_GIFT_PASSES: 'read:gift_passes',
  /** See a gift pass's full code. Admin and CSR only. */
  READ_GIFT_PASS_CODES: 'read:gift_pass_codes',
  /** Create a comp gift pass. Admin and CSR. Needs `read:gift_passes` as well. */
  ISSUE_GIFT_PASSES: 'issue:gift_passes',
  /** Void an unredeemed gift pass. Admin and CSR. Needs `read:gift_passes` as well. */
  VOID_GIFT_PASSES: 'void:gift_passes',
  /** Send a gift pass email again. Admin and CSR. Needs `read:gift_passes` as well. */
  RESEND_GIFT_PASSES: 'resend:gift_passes',
  /** Refund a booking to a gift card (BON-147). CSR / Regional Director /
   *  Admin, breadth-confined like every other booking write. */
  REFUND_BOOKINGS: 'refund:bookings',
  /** Move a booking to another same-price workshop (BON-146). CSR / Regional
   *  Director / Admin, breadth-confined on BOTH the source and the destination
   *  workshop. No `write:all` fallback on the route — it mirrors how refunds
   *  were granted. */
  REBOOK_BOOKINGS: 'rebook:bookings',
  /** Move a booking after the 10 AM venue-local cutoff on the day of its
   *  workshop (BON-146). ADDITIONAL to `rebook:bookings`, never a substitute:
   *  holding this alone does not let anyone rebook. CSR + Admin only —
   *  deliberately NOT Regional Directors, because CSRs are the ones on the
   *  phone with a customer needing a same-day move. */
  OVERRIDE_REBOOK_CUTOFF: 'override:rebook-cutoff',
  READ_STAFF: 'read:staff',
  WRITE_STAFF: 'write:staff',
  /** See staff pay rates and who is salaried. Admin, Regional Director, Finance. */
  READ_PAY_RATES: 'read:pay_rates',
  /** Change pay rates and the salaried flag. Admin and Regional Director; a
   *  Regional Director is further limited by the API to their own regions. */
  WRITE_PAY_RATES: 'write:pay_rates',
  READ_REGIONS: 'read:regions',
  WRITE_REGIONS: 'write:regions',
  DELETE_REGIONS: 'delete:regions',
  READ_SYNC: 'read:sync',
  WRITE_SYNC: 'write:sync',
  READ_SETTINGS: 'read:settings',
  CREATE_SETTINGS: 'create:settings',
  UPDATE_SETTINGS: 'update:settings',
  DELETE_SETTINGS: 'delete:settings',
  READ_USER_REGIONS: 'read:user_regions',
  WRITE_USER_REGIONS: 'write:user_regions',
  /** National ticket discounts. Admin + Marketing only, for reading and writing. */
  READ_TICKET_SALES_CAMPAIGNS: 'read:ticket_sales_campaigns',
  WRITE_TICKET_SALES_CAMPAIGNS: 'write:ticket_sales_campaigns',
  /** Gift-pass bundle offers. Admin + Marketing only. */
  READ_GIFT_PASS_BUNDLES: 'read:gift_pass_bundles',
  WRITE_GIFT_PASS_BUNDLES: 'write:gift_pass_bundles',
  READ_PROMOTIONS: 'read:promotions',
  WRITE_PROMOTIONS: 'write:promotions',
  DELETE_PROMOTIONS: 'delete:promotions',
  // User Management & Permissions (BON-46). Backend-checked; surfaced here
  // so frontend can gate UI affordances (invite button, edit dialog, etc.).
  // `read:internal_region_ids` is intentionally omitted — that scope is
  // M2M-only (the post-login Action's client) and never appears on a user JWT.
  INVITE_USERS: 'invite:users',
  MANAGE_USER_ROLES: 'manage:user_roles',
  MANAGE_USER_REGIONS: 'manage:user_regions',
  DEACTIVATE_USERS: 'deactivate:users',
  READ_AUDIT_LOG: 'read:audit_log',
  /** Sentinel — interpreted by backend `attachUserScope` to bypass region
   *  filtering on list endpoints. Granted only to Admin. Don't add new code
   *  paths gated on it; use specific permissions for finer control. */
  READ_ALL: 'read:all',
  /** Admin WRITE sentinel — held only by Admin, the cross-region write
   *  counterpart to `read:all`. Managers hold specific scopes (e.g.
   *  `write:workshops`) instead. Surfaced here so the FE can mirror the
   *  server's admin-only overrides (BON-215: bypass the 6-week publish lead). */
  WRITE_ALL: 'write:all',
  // Workshop Dashboards (BON-64). Backend-checked at route layer; surfaced
  // here so the frontend can gate the dashboards pages and the mode switch.
  READ_DASHBOARDS: 'read:dashboards',
  /** The national Daily sales report under Finance. Admin, Finance, Marketing and
   *  Regional Director hold it; CSR and Instructor do not. */
  READ_SALES_REPORTS: 'read:sales_reports',
  /** Cross-region READ-ONLY for the Workshop Dashboards Q2 mode (RD viewing
   *  other regions). Grants read across all regions, NOT edit. Admins
   *  inherit this via `read:all`. */
  READ_ALL_REGIONS: 'read:all_regions',
  // ADR-0016 region breadth — the axis orthogonal to capability. `all` skips
  // region filtering; `assigned` limits to the caller's staff_regions. During
  // the router-by-router migration `read:all` / `read:all_regions` are treated
  // as transitional aliases for the read/write breadth by the backend.
  /** Read across ALL regions (region filter skipped). */
  BREADTH_READ_ALL: 'breadth_read:all',
  /** Read limited to the caller's assigned regions. */
  BREADTH_READ_ASSIGNED: 'breadth_read:assigned',
  /** Write across ALL regions (region filter skipped). */
  BREADTH_WRITE_ALL: 'breadth_write:all',
  /** Write limited to the caller's assigned regions. */
  BREADTH_WRITE_ASSIGNED: 'breadth_write:assigned',
  // App mode (BON-561) — which of the two views of Nebari a viewer may enter.
  // Granted explicitly rather than inferred from another scope, so adding a
  // role means deciding what it can see instead of discovering it later.
  /** Enter admin mode — the organizational view. Every role but Instructor. */
  ACCESS_ADMIN_MODE: 'access:admin_mode',
  /** Enter teacher mode — the instructor view. Instructor, Regional Director
   *  and Admin. Office roles do not hold it: a teacher workspace about someone
   *  who never teaches has nothing in it. */
  ACCESS_TEACHER_MODE: 'access:teacher_mode',
} as const;

export type PermissionString = (typeof Permission)[keyof typeof Permission];
